import React from 'react';
import { usePerformanceMonitoring } from '@/hooks/usePerformanceMonitoring';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  TrendingUp, 
  TrendingDown,
  Zap,
  Database,
  Server,
  Wifi,
  WifiOff,
  RefreshCw,
  Lightbulb,
  X
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { format } from 'date-fns';

export const AdvancedPerformanceAnalytics = () => {
  const {
    metrics,
    alerts,
    recommendations,
    isConnected,
    lastUpdate,
    markRecommendationImplemented,
    markAlertResolved,
    isLoading,
    refetch
  } = usePerformanceMonitoring();

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-destructive text-destructive-foreground';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-white';
      case 'low': return 'bg-blue-500 text-white';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  const getMetricIcon = (metricName: string) => {
    switch (metricName) {
      case 'cpu_usage': return <Server className="h-4 w-4" />;
      case 'memory_usage': return <Database className="h-4 w-4" />;
      case 'api_response_time': return <Zap className="h-4 w-4" />;
      case 'active_processing': return <Activity className="h-4 w-4" />;
      default: return <TrendingUp className="h-4 w-4" />;
    }
  };

  const formatMetricValue = (value: number, metricName: string) => {
    switch (metricName) {
      case 'cpu_usage':
      case 'memory_usage':
      case 'disk_usage':
        return `${value.toFixed(1)}%`;
      case 'api_response_time':
        return `${value.toFixed(0)}ms`;
      default:
        return value.toFixed(0);
    }
  };

  const getMetricTrend = (metricName: string) => {
    const recentMetrics = metrics
      .filter(m => m.metric_name === metricName)
      .slice(0, 10);
    
    if (recentMetrics.length < 2) return null;
    
    const latest = recentMetrics[0]?.value || 0;
    const previous = recentMetrics[1]?.value || 0;
    
    return latest > previous ? 'up' : 'down';
  };

  const chartData = React.useMemo(() => {
    const groupedMetrics = metrics.reduce((acc, metric) => {
      const timestamp = new Date(metric.created_at).getTime();
      if (!acc[timestamp]) {
        acc[timestamp] = { timestamp, time: format(new Date(metric.created_at), 'HH:mm') };
      }
      acc[timestamp][metric.metric_name] = metric.value;
      return acc;
    }, {} as Record<number, any>);

    return Object.values(groupedMetrics)
      .sort((a, b) => a.timestamp - b.timestamp)
      .slice(-20);
  }, [metrics]);

  const latestMetrics = React.useMemo(() => {
    const grouped = metrics.reduce((acc, metric) => {
      if (!acc[metric.metric_name] || new Date(metric.created_at) > new Date(acc[metric.metric_name].created_at)) {
        acc[metric.metric_name] = metric;
      }
      return acc;
    }, {} as Record<string, any>);
    
    return Object.values(grouped);
  }, [metrics]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Performance Analytics</h2>
            <p className="text-muted-foreground">Real-time system performance monitoring and optimization</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                <div className="h-4 w-4 bg-muted animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-8 w-16 bg-muted animate-pulse rounded mb-2" />
                <div className="h-3 w-24 bg-muted animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Performance Analytics</h2>
          <p className="text-muted-foreground">Real-time system performance monitoring and optimization</p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-2">
            {isConnected ? (
              <>
                <Wifi className="h-4 w-4 text-green-500" />
                <span className="text-sm text-green-600">Connected</span>
              </>
            ) : (
              <>
                <WifiOff className="h-4 w-4 text-red-500" />
                <span className="text-sm text-red-600">Disconnected</span>
              </>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={refetch}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {lastUpdate && (
        <div className="text-sm text-muted-foreground">
          Last updated: {format(lastUpdate, 'PPpp')}
        </div>
      )}

      {/* Key Metrics Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {latestMetrics.map((metric) => {
          const trend = getMetricTrend(metric.metric_name);
          return (
            <Card key={metric.id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium capitalize">
                  {metric.metric_name.replace('_', ' ')}
                </CardTitle>
                {getMetricIcon(metric.metric_name)}
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatMetricValue(metric.value, metric.metric_name)}
                </div>
                <div className="flex items-center text-xs text-muted-foreground">
                  {trend === 'up' ? (
                    <TrendingUp className="h-3 w-3 mr-1 text-green-500" />
                  ) : trend === 'down' ? (
                    <TrendingDown className="h-3 w-3 mr-1 text-red-500" />
                  ) : null}
                  <span>
                    {format(new Date(metric.created_at), 'p')}
                  </span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="metrics" className="space-y-4">
        <TabsList>
          <TabsTrigger value="metrics">Performance Metrics</TabsTrigger>
          <TabsTrigger value="alerts" className="relative">
            Alerts
            {alerts.length > 0 && (
              <Badge variant="destructive" className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">
                {alerts.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="recommendations" className="relative">
            Recommendations
            {recommendations.length > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">
                {recommendations.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="metrics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>System Performance Trends</CardTitle>
              <CardDescription>
                Real-time performance metrics over time
              </CardDescription>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="time" 
                      fontSize={12}
                    />
                    <YAxis fontSize={12} />
                    <Tooltip 
                      labelFormatter={(label) => `Time: ${label}`}
                      formatter={(value: number, name: string) => [
                        formatMetricValue(value, name),
                        name.replace('_', ' ').toUpperCase()
                      ]}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="cpu_usage" 
                      stroke="hsl(var(--chart-1))" 
                      strokeWidth={2}
                      name="CPU Usage"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="memory_usage" 
                      stroke="hsl(var(--chart-2))" 
                      strokeWidth={2}
                      name="Memory Usage"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="api_response_time" 
                      stroke="hsl(var(--chart-3))" 
                      strokeWidth={2}
                      name="Response Time"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-40 text-muted-foreground">
                  No performance data available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4">
          {alerts.length === 0 ? (
            <Card>
              <CardContent className="flex items-center justify-center h-40">
                <div className="text-center">
                  <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                  <h3 className="text-lg font-medium">All systems operational</h3>
                  <p className="text-muted-foreground">No active alerts at this time</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {alerts.map((alert) => (
                <Alert key={alert.id} className="relative">
                  <AlertTriangle className="h-4 w-4" />
                  <div className="flex-1">
                    <AlertTitle className="flex items-center gap-2">
                      {alert.title}
                      <Badge className={getSeverityColor(alert.severity)}>
                        {alert.severity}
                      </Badge>
                    </AlertTitle>
                    <AlertDescription className="mt-2">
                      {alert.description}
                    </AlertDescription>
                    <div className="mt-2 text-xs text-muted-foreground">
                      {format(new Date(alert.created_at), 'PPpp')}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => markAlertResolved(alert.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </Alert>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="recommendations" className="space-y-4">
          {recommendations.length === 0 ? (
            <Card>
              <CardContent className="flex items-center justify-center h-40">
                <div className="text-center">
                  <Lightbulb className="h-12 w-12 mx-auto text-blue-500 mb-4" />
                  <h3 className="text-lg font-medium">No recommendations</h3>
                  <p className="text-muted-foreground">Your system is running optimally</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {recommendations.map((rec) => (
                <Card key={rec.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Lightbulb className="h-5 w-5" />
                        {rec.title}
                        <Badge className={getSeverityColor(rec.severity)}>
                          {rec.severity}
                        </Badge>
                      </CardTitle>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => markRecommendationImplemented(rec.id)}
                      >
                        Mark Implemented
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="mb-3">
                      {rec.description}
                    </CardDescription>
                    {rec.action_required && (
                      <>
                        <Separator className="my-3" />
                        <div className="bg-muted/50 p-3 rounded-md">
                          <h4 className="font-medium mb-2">Recommended Action:</h4>
                          <p className="text-sm">{rec.action_required}</p>
                        </div>
                      </>
                    )}
                    <div className="mt-3 text-xs text-muted-foreground">
                      Generated: {format(new Date(rec.created_at), 'PPpp')}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};